// src/pages/Purchase/Purchase Return/PurchaseReturnRecord/components/PurchaseReturnRecordTable.js
import React, { useMemo, useState } from "react";
import { Table, Button, Tooltip, Modal, Descriptions } from "antd";
import { DeleteOutlined, EditOutlined, EyeOutlined, InfoCircleOutlined, RedoOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";

import { useNavigate } from "react-router-dom";
import { accessChecker, formatToCompanyTimezone, numberFormatTax } from "../../../../../lib/functions";
import {
  checkPurchaseReturnDelete,
  deletePurchaseReturn,
  fetchPurchaseReturnRecord,
  retryTatmeenPurchaseReturn
} from "../PurchaseReturnRecordThunk";
import Swal from "sweetalert2";
import { useIsAdminUser } from "../../../../../lib/accessList";
import { useTxLock } from "../../../../../hooks/useTxLock";
import VoucherPdfActions from "../../../../../components/common/VoucherPdfActions";
import "../../../../../components/common/bt.css";

const PurchaseReturnRecordTable = ({ printMode = false }) => {
  const { list, loading, filters } = useSelector(
    (state) => state.purchaseReturnRecord,
  );
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });
  const [tatmeenModal, setTatmeenModal] =
    useState({
      open: false,
      record: null,
    });

  const navigate = useNavigate();
  const dispatch = useDispatch();
  // const [isAdminUser, isAdminUserSet] = useState(false);
  const authInfo = useSelector((state) => state.auth.auth_info);
  const isIndia = authInfo?.country?.toLowerCase() === "india";
  const isGstEnabled = authInfo?.gst_enabled === "yes";
  const showGstColumns = isIndia && isGstEnabled;
  const showTax = !isIndia || isGstEnabled;
  const taxLabel = isIndia ? "GST" : "VAT";
  const money = (v) => Number(v || 0).toFixed(2);
  const isAdminUser = useIsAdminUser();
  const canEdit = authInfo?.can_edit === 1;
  const canDelete = authInfo?.can_delete === 1;
  const isTatmeenUAE =
    authInfo?.is_tatmeen_enabled === "yes" &&
    authInfo?.country === "United Arab Emirates" && (authInfo?.subscription == 'PharmaERP_Pro' || authInfo?.subscription == 'PharmaERP_Standard' || authInfo?.subscription == 'PharmaERP_Basic');

  const hasTatmeenEntryAccess = accessChecker("tatmeen_entry");

  const handleTableChange = (pagination) => {
    setPagination(pagination);
  };

  const handleDelete = async (record) => {
    const pur_r_id = record.pur_r_id;
    if (!pur_r_id) return;
    const confirm = await Swal.fire({
      title: "Delete Purchase Return?",
      text: "This action cannot be undone",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ff4d4f",
      confirmButtonText: "Yes, Delete",
    });
    if (!confirm.isConfirmed) return;
    // 🔍 Pre-check
    const checkRes = await dispatch(
      checkPurchaseReturnDelete(pur_r_id),
    ).unwrap();

    if (checkRes.message !== "yes") {
      Swal.fire(
        "Cannot Delete",
        "Stock / serials already moved. Delete not allowed.",
        "warning",
      );
      return;
    }
    // ❌ Delete
    await dispatch(deletePurchaseReturn(pur_r_id)).unwrap();

    Swal.fire("Deleted", "Purchase Return deleted successfully", "success");

    // 🔄 Reload report
    dispatch(
      fetchPurchaseReturnRecord({
        selectedFilterType: filters.filterType,
        selectedRecordType: filters.recordType,
        filters,
      }),
    );
  };
  const { isLocked: isTxLocked, lockDateFormatted } = useTxLock();

  const handleRetryTatmeen = async () => {
    const result = await Swal.fire({
      title: "Retry Tatmeen Sync?",
      text: `Purchase Return ${tatmeenModal.record.vch_no} will be reprocessed.`,
      icon: "question",
      showCancelButton: true,
    });

    if (!result.isConfirmed) return;

    try {
      await dispatch(
        retryTatmeenPurchaseReturn(
          tatmeenModal.record.pur_r_id
        )
      ).unwrap();

      Swal.fire(
        "Success",
        "Tatmeen retry has been queued successfully.",
        "success"
      );

      setTatmeenModal({
        open: false,
        record: null,
      });

      dispatch(
        fetchPurchaseReturnRecord({
          selectedFilterType: filters.filterType,
          selectedRecordType: filters.recordType,
          filters,
        })
      );
    } catch (err) {
      const message =
        typeof err === "string"
          ? err
          : err?.message
            ? err.message
            : err?.response?.data?.message
              ? err.response.data.message
              : "Retry failed";

      Swal.fire("Failed", message, "error");
    }
  };

  // -----------------------
  // Columns for WITHOUT ITEM (summary)
  // -----------------------
  const summaryColumns = [
    { title: "No.", render: (_, __, index) => index + 1, width: 60 },
    { title: "Voucher No", dataIndex: "vch_no" },

    {
      title: "Date",
      dataIndex: "date",
      render: (v) => (v ? formatToCompanyTimezone(v).split(" ")[0] : "-"),
    },

    { title: "Supplier", dataIndex: "supplier" },
    { title: "Created By", dataIndex: "createdBy" },

    {
      title: "Sub Total",
      dataIndex: "subtotal",
      render: (val) => Number(val).toFixed(2),
    },

    {
      title: "Discount",
      dataIndex: "discount",
      render: (val) => Number(val).toFixed(2),
    },

    showTax && {
      title: taxLabel,
      dataIndex: "vat",
      render: (val) => Number(val).toFixed(2),
    },

    showGstColumns && {
      title: "CGST",
      dataIndex: "cgst_amount",
      render: numberFormatTax,
      align: "right",
    },

    showGstColumns && {
      title: "SGST",
      dataIndex: "sgst_amount",
      render: numberFormatTax,
      align: "right",
    },

    showGstColumns && {
      title: "IGST",
      dataIndex: "igst_amount",
      render: numberFormatTax,
      align: "right",
    },

    {
      title: "Other Service",
      dataIndex: "service",
      render: (val) => Number(val).toFixed(2),
    },

    {
      title: "Total",
      dataIndex: "total",
      render: (val) => Number(val).toFixed(2),
    },

    {
      title: "Paid",
      dataIndex: "paid",
      render: (val) => Number(val).toFixed(2),
    },

    {
      title: "Due",
      dataIndex: "due",
      render: (val) => Number(val).toFixed(2),
    },
    hasTatmeenEntryAccess && isTatmeenUAE && {
      title: "Tatmeen Status",
      dataIndex: "tatmeen_sent_status",
      render: (status) => {
        const colorMap = {
          sent: "#52c41a",
          pending: "#faad14",
          failed: "#ff4d4f",
          cancelled: "#8c8c8c",
        };

        return (
          <span
            style={{
              color: colorMap[status] || "#000",
              fontWeight: 500,
            }}
          >
            {status || "-"}
          </span>
        );
      },
    },
    !printMode && {
      title: "Actions",
      className: "no-print",
      render: (_, record) => {
        const txLocked = isTxLocked(record?.date);

        return (
          <div className="action-icons">
            {isTatmeenUAE && hasTatmeenEntryAccess && (
              <Tooltip title="Tatmeen Details">
                <Button
                  type="link"
                  icon={<InfoCircleOutlined />}
                  onClick={() =>
                    setTatmeenModal({
                      open: true,
                      record,
                    })
                  }
                />
              </Tooltip>
            )}
            <Tooltip title="View Purchase Return">
              <Button
                type="link"
                icon={<EyeOutlined />}
                onClick={() =>
                  navigate(
                    `/purchase/quick-purchase-return-voucher/${record.pur_r_id}`
                  )
                }
              />
            </Tooltip>

            {/* EDIT */}
            <Tooltip
              title={
                txLocked
                  ? `This voucher is locked. Transactions up to ${lockDateFormatted} cannot be modified.`
                  : "Edit Purchase Return"
              }
            >
              <Button
                type="link"
                disabled={!(isAdminUser || canEdit) || txLocked}
                icon={<EditOutlined />}
                onClick={() => {
                  if (txLocked) return;

                  navigate(`/purchase-return/${record.pur_r_id}?tab=entry`);
                }}
              />
            </Tooltip>

            {/* DELETE */}
            <Tooltip
              title={
                txLocked
                  ? `This voucher is locked. Transactions up to ${lockDateFormatted} cannot be modified.`
                  : "Delete Purchase Return"
              }
            >
              <Button
                type="link"
                danger
                disabled={!(isAdminUser || canDelete) || txLocked}
                icon={<DeleteOutlined />}
                onClick={() => {
                  if (txLocked) return;

                  handleDelete(record);
                }}
              />
            </Tooltip>

            <VoucherPdfActions
              url={`/print/purchase-return/${record.pur_r_id}`}
              fileName={`PurchaseReturn_${record.vch_no || record.pur_r_id}.pdf`}
            />
          </div>
        );
      },
    },
  ].filter(Boolean);

  // -----------------------
  // Columns for WITH ITEM (detailed)
  // -----------------------
  const withItemColumns = [
    { title: "No.", render: (_, __, index) => index + 1, width: 60 },
    { title: "Voucher No", dataIndex: "vch_no" },
    {
      title: "Date",
      dataIndex: "date",
      render: (v) => (v ? formatToCompanyTimezone(v).split(" ")[0] : "-"),
    },
    { title: "Supplier", dataIndex: "supplier" },
    { title: "Created By", dataIndex: "createdBy" },
    { title: "Item", dataIndex: "item" },
    { title: "Rate", dataIndex: "rate" },
    ...(list.some((row) => row.discount && row.discount !== "0%")
      ? [{ title: "Discount%", dataIndex: "discount" }]
      : []),
    ...(list.some((row) => row.vat && row.vat !== "0%")
      ? [{ title: "VAT%", dataIndex: "vat" }]
      : []),
    { title: "Qty", dataIndex: "qty" },
    { title: "Total", dataIndex: "total" },
    !printMode && {
      title: "Actions",
      className: "no-print",
      render: (_, record) => (
        <div className="action-icons">
          <Tooltip title="View Purchase Return">
            <Button
              type="link"
              icon={<EyeOutlined />}
              onClick={() =>
                navigate(
                  `/purchase/quick-purchase-return-voucher/${record.pur_r_id}`,
                )
              }
            />
          </Tooltip>

          {/* EDIT */}
          {/* <Tooltip title="Edit Sales Return">
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => {
                dispatch(fetchSalesReturnWithDetails(record.sale_r_id)).then((res) => {
                  if (res.meta.requestStatus === "fulfilled") {
                    dispatch(setIsEditSalesReturn(true));
                    dispatch(setSelectedSalesReturnRecord(res.payload));
                    navigate(`/sales-return/${record.sale_r_id}`);
                  }
                });
              }}
            />
          </Tooltip> */}


          <Tooltip title="Edit Purchase Return">
            <Button
              type="link"
              icon={<EditOutlined />}
              disabled={!(isAdminUser || canEdit)}

              onClick={() =>
                navigate(`/purchase-return/${record.pur_r_id}?tab=entry`)
              }
            />
          </Tooltip>


          {/* DELETE */}

          <Tooltip title="Delete Purchase Return">
            <Button
              type="link"
              danger
              disabled={!(isAdminUser || canDelete)}
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record)}
            />
          </Tooltip>

          <VoucherPdfActions
            url={`/print/purchase-return/${record.pur_r_id}`}
            fileName={`PurchaseReturn_${record.vch_no || record.pur_r_id}.pdf`}
          />

        </div>
      ),
    },
  ].filter(Boolean);

  const byItemColumns = useMemo(
    () => [
      { title: "No.", render: (_, __, i) => i + 1 },
      { title: "Voucher No", dataIndex: "vch_no" },
      {
        title: "Date",
        dataIndex: "date",
        render: (v) => (v ? formatToCompanyTimezone(v).split(" ")[0] : "-"),
      },
      { title: "Supplier", dataIndex: "supplier" },
      { title: "Item Name", dataIndex: "item_name" },
      { title: "Item Code", dataIndex: "display_text" },
      { title: "Qty", dataIndex: "item_qty", align: "right" },
      { title: "Rate", dataIndex: "item_rate", render: money, align: "right" },
      { title: "Discount%", dataIndex: "item_discount_per", align: "right" },
      showTax && { title: "VAT%", dataIndex: "item_tax_per", align: "right" },
      { title: "Group", dataIndex: "grpname", align: "right" },
      { title: "Category", dataIndex: "categoryName", align: "right" },
      {
        title: "Total",
        dataIndex: "item_total",
        render: money,
        align: "right",
      },
    ].filter(Boolean),
    [showTax],
  );

  const byTagColumns = useMemo(
    () => [
      ...byItemColumns,
      { title: "Tag Group", dataIndex: "group_name" },
      { title: "Tag Field", dataIndex: "field_name" },
    ],
    [byItemColumns],
  );


  const columns = useMemo(() => {
    switch (filters.filterType) {
      case "By Group":
        return byItemColumns;
      case "By Category":
        return byItemColumns;
      case "By Item":
        return byItemColumns;
      case "By Tag":
        return byTagColumns;
      default:
        if (filters.recordType === "with item") return withItemColumns;
        return summaryColumns;
    }
  }, [filters, summaryColumns, withItemColumns, byItemColumns, byTagColumns]);

  // -----------------------
  // Grand Totals
  // -----------------------
  let totals;
  if (filters.recordType === "with item") {
    totals = list.reduce(
      (acc, row) => {
        acc.total += Number(row.total || 0);
        acc.master_qty += Number(row.pur_r_qty || 0);
        acc.base_qty += Number(row.pur_r_qty || 0);
        return acc;
      },
      { total: 0, master_qty: 0, base_qty: 0 },
    );
  } else if (
    ["By Item", "By Group", "By Category", "By Tag"].includes(
      filters.filterType,
    )
  ) {
    totals = list.reduce(
      (acc, row) => {
        acc.total += Number(row.total || 0);
        return acc;
      },
      { total: 0 },
    );
  } else {
    totals = list.reduce(
      (acc, row) => {
        acc.subtotal += Number(row.subtotal || 0);
        acc.discount += Number(row.discount || 0);
        acc.vat += Number(row.vat || 0);
        acc.cgst += Number(row.cgst_amount || 0);
        acc.sgst += Number(row.sgst_amount || 0);
        acc.igst += Number(row.igst_amount || 0);
        acc.service += Number(row.service || 0);
        acc.total += Number(row.total || 0);
        acc.paid += Number(row.paid || 0);
        acc.due += Number(row.due || 0);
        return acc;
      },
      {
        subtotal: 0,
        discount: 0,
        vat: 0,
        cgst: 0,
        sgst: 0,
        igst: 0,
        service: 0,
        total: 0,
        paid: 0,
        due: 0,
      },
    );
  }

  // -----------------------
  // Expandable rows (for WITH ITEM)
  // -----------------------
  const expandable =
    filters.recordType === "with item"
      ? {
        expandedRowRender: (record) => (
          <div style={{ fontSize: "12px", color: "#555" }}>
            {record.unitDetails}
          </div>
        ),
        rowExpandable: (record) => !!record.unitDetails,
      }
      : undefined;

  if (printMode) {
    const printColumns = columns.filter(
      (col) =>
        !["Created By", "CGST", "SGST", "IGST", "Other Service"].includes(
          col.title
        )
    );

    const PRINT_COLUMN_WEIGHTS = {
      "No.": 3,
      "Voucher No": 9,
      Date: 8,
      Supplier: 12,
      Item: 10,
      "Item Name": 10,
      "Item Code": 9,
      Rate: 6,
      "Sub Total": 7,
      Discount: 6,
      "Discount%": 6,
      VAT: 6,
      "VAT%": 6,
      GST: 6,
      Total: 7,
      Paid: 6,
      Due: 6,
      Qty: 5,
      Group: 7,
      Category: 7,
      "Tag Group": 8,
      "Tag Field": 8,
    };

    const rawWeights = printColumns.map(
      (col) => PRINT_COLUMN_WEIGHTS[col.title] || 7
    );
    const weightSum = rawWeights.reduce((sum, w) => sum + w, 0);
    const colWidths = rawWeights.map((w) => (w / weightSum) * 100);

    const isByDetail = ["By Item", "By Group", "By Category", "By Tag"].includes(
      filters.filterType
    );
    const isWithItem = filters.recordType === "with item";

    return (
      <table className="ledger-print-table purchase-return-print-table">
        <colgroup>
          {colWidths.map((w, idx) => (
            <col key={idx} style={{ width: `${w}%` }} />
          ))}
        </colgroup>

        <thead>
          <tr className="ledger-print-heading">
            {printColumns.map((col, idx) => (
              <td
                key={idx}
                style={{
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                  ...(col.align === "right" ? { textAlign: "right" } : {}),
                }}
              >
                {col.title}
              </td>
            ))}
          </tr>
        </thead>

        <tbody>
          {list.map((row, rowIndex) => (
            <tr key={row.id || rowIndex}>
              {printColumns.map((col, colIndex) => (
                <td
                  key={colIndex}
                  style={{
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                    ...(col.align === "right" ? { textAlign: "right" } : {}),
                  }}
                >
                  {col.render
                    ? col.render(row[col.dataIndex], row, rowIndex)
                    : row[col.dataIndex]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>

        <tfoot>
          {isWithItem ? (
            <tr className="ledger-total-row">
              <td
                colSpan={printColumns.length - 1}
                style={{ textAlign: "right" }}
              >
                Grand Total : Master U. Qty = {totals.master_qty}, Base U. Qty ={" "}
                {totals.base_qty}
              </td>
              <td style={{ textAlign: "right" }}>{totals.total.toFixed(2)}</td>
            </tr>
          ) : isByDetail ? (
            <tr className="ledger-total-row">
              <td
                colSpan={printColumns.length - 1}
                style={{ textAlign: "right" }}
              >
                Grand Total
              </td>
              <td style={{ textAlign: "right" }}>{totals.total.toFixed(2)}</td>
            </tr>
          ) : (
            <tr className="ledger-total-row">
              <td colSpan={4} style={{ textAlign: "right" }}>
                Grand Total
              </td>
              <td style={{ textAlign: "right" }}>{totals.subtotal.toFixed(2)}</td>
              <td style={{ textAlign: "right" }}>{totals.discount.toFixed(2)}</td>
              {showTax && (
                <td style={{ textAlign: "right" }}>{totals.vat.toFixed(2)}</td>
              )}
              <td style={{ textAlign: "right" }}>{totals.total.toFixed(2)}</td>
              <td style={{ textAlign: "right" }}>{totals.paid.toFixed(2)}</td>
              <td style={{ textAlign: "right" }}>{totals.due.toFixed(2)}</td>
            </tr>
          )}
        </tfoot>
      </table>
    );
  }

  return (
    <Table
      rowKey="id"
      columns={columns}
      dataSource={list}
      loading={loading}
      pagination={{
        ...pagination,
        showSizeChanger: true,
        pageSizeOptions: ["10", "20", "50"],
      }}
      onChange={handleTableChange}

      data-testid="purchase-return-record-table"
      bordered
      expandable={expandable}
      scroll={printMode ? undefined : { x: "max-content" }}
      size={printMode ? "small" : "middle"}
      summary={() => {
        if (filters.recordType === "with item") {
          return (
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={10}>
                <strong>
                  Grand Total : Master U. Qty = {totals.master_qty}, Base U. Qty
                  = {totals.base_qty}
                </strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={10}>
                <strong>{totals.total.toFixed(2)}</strong>
              </Table.Summary.Cell>
              {!printMode && (
                <Table.Summary.Cell index={10}></Table.Summary.Cell>
              )}
            </Table.Summary.Row>
          );
        } else if (
          ["By Item", "By Group", "By Category", "By Tag"].includes(
            filters.filterType,
          )
        ) {
          return (
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={6}>
                <strong>Grand Total</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={6} align="right">
                <strong>{totals.total.toFixed(2)}</strong>
              </Table.Summary.Cell>
            </Table.Summary.Row>
          );
        } else {
          const taxColOffset = (showTax ? 1 : 0) + (showGstColumns ? 3 : 0);
          return (
            <>
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={5}>
                  <strong>Grand Total</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={5}>
                  {totals.subtotal.toFixed(2)}
                </Table.Summary.Cell>
                <Table.Summary.Cell index={6}>
                  {totals.discount.toFixed(2)}
                </Table.Summary.Cell>
                {showTax && (
                  <Table.Summary.Cell index={7}>
                    {totals.vat.toFixed(2)}
                  </Table.Summary.Cell>
                )}

                {showGstColumns && (
                  <Table.Summary.Cell index={8} align="right">
                    {numberFormatTax(totals.cgst || 0)}
                  </Table.Summary.Cell>
                )}

                {showGstColumns && (
                  <Table.Summary.Cell index={9} align="right">
                    {numberFormatTax(totals.sgst || 0)}
                  </Table.Summary.Cell>
                )}

                {showGstColumns && (
                  <Table.Summary.Cell index={10} align="right">
                    {numberFormatTax(totals.igst || 0)}
                  </Table.Summary.Cell>
                )}

                <Table.Summary.Cell index={7 + taxColOffset}>
                  {totals?.service.toFixed(2)}
                </Table.Summary.Cell>
                <Table.Summary.Cell index={8 + taxColOffset}>
                  {totals.total.toFixed(2)}
                </Table.Summary.Cell>
                <Table.Summary.Cell index={9 + taxColOffset}>
                  {totals.paid.toFixed(2)}
                </Table.Summary.Cell>
                <Table.Summary.Cell index={10 + taxColOffset}>
                  {totals.due.toFixed(2)}
                </Table.Summary.Cell>
                {!printMode && (
                  <Table.Summary.Cell index={11 + taxColOffset}></Table.Summary.Cell>
                )}
              </Table.Summary.Row>

              <Modal
                title="Tatmeen Details"
                open={tatmeenModal.open}
                onCancel={() =>
                  setTatmeenModal({
                    open: false,
                    record: null,
                  })
                }
                footer={
                  hasTatmeenEntryAccess &&
                    (
                      tatmeenModal.record?.tatmeen_sent_status === "failed" ||
                      tatmeenModal.record?.tatmeen_sent_status === "not_started"
                    )
                    ? [
                      <Button
                        key="retry"
                        type="primary"
                        icon={<RedoOutlined />}
                        onClick={handleRetryTatmeen}
                      >
                        Mark Return Entry
                      </Button>,
                    ]
                    : null
                }
              >
                {tatmeenModal.record && (
                  <Descriptions
                    bordered
                    column={1}
                    title="Tatmeen Details"
                  >
                    {/* <Descriptions.Item label="Original SSCC">
                      {tatmeenModal.record.original_sscc || "-"}
                    </Descriptions.Item> */}

                    <Descriptions.Item label="Return SSCC">
                      {tatmeenModal.record.sscc_code || "-"}
                    </Descriptions.Item>

                    <Descriptions.Item label="Tatmeen Status">
                      {tatmeenModal.record.tatmeen_sent_status || "-"}
                    </Descriptions.Item>

                    <Descriptions.Item label="SSCC Status">
                      {tatmeenModal.record.tatmeen_sscc_status || "-"}
                    </Descriptions.Item>

                    <Descriptions.Item label="Message ID">
                      {tatmeenModal.record.tatmeen_message_id || "-"}
                    </Descriptions.Item>

                    <Descriptions.Item label="Retry Count">
                      {tatmeenModal.record.tatmeen_retry_count || 0}
                    </Descriptions.Item>

                    <Descriptions.Item label="Failure Reason">
                      {tatmeenModal.record.tatmeen_failure_reason ? (
                        <div
                          style={{
                            maxHeight: 200,
                            overflowY: "auto",
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                            fontFamily: "monospace",
                            fontSize: 11,
                            background: "#fafafa",
                            padding: 8,
                            borderRadius: 4,
                            border: "1px solid #f0f0f0",
                          }}
                        >
                          {tatmeenModal.record.tatmeen_failure_reason}
                        </div>
                      ) : (
                        "-"
                      )}
                    </Descriptions.Item>

                    <Descriptions.Item label="Manual Action Required">
                      {tatmeenModal.record.tatmeen_manual_action_required || "-"}
                    </Descriptions.Item>

                    {/* <Descriptions.Item label="Last Retry At">
                      {tatmeenModal.record.tatmeen_last_retry_at || "-"}
                    </Descriptions.Item> */}
                  </Descriptions>
                )}
              </Modal>
            </>
          );
        }
      }}
    />
  );
};

export default PurchaseReturnRecordTable;
